// ============================================================
// CommunityMembers — MEN-259 [V1-06b] slice 2 : attribution de
// rôles aux membres d'une communauté (surface admin instance).
//
// Acceptance #1 : assigner/retirer un rôle persiste côté serveur
// (PUT/DELETE /admin/communities/:cid/members/:uid/roles/:rid),
// optimistic update + rollback si 4xx, et synchro temps réel via
// l'événement Socket.IO `member:roles-updated`.
//
// Toute la donnée reste serveur : la liste des rôles d'un membre
// est calculée en base (table MemberRole), jamais déduite côté
// client ; l'UI ne fait que basculer une assignation.
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminMember, AdminRole } from '@siverix/api-client';
import { ApiError } from '@siverix/api-client';
import type { AuditLogLive } from '@siverix/shared';
import { api } from '../api';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-border)',
};
const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontSize: '12px',
  cursor: 'pointer',
};

function memberLabel(m: AdminMember): string {
  return m.user?.displayName ?? m.user?.username ?? m.userId.slice(0, 8);
}
function colorIntToHex(n: number): string {
  return `#${Math.max(0, Math.min(0xffffff, n)).toString(16).padStart(6, '0')}`;
}

/**
 * [2.10.0] L'entrée du journal d'audit touche-t-elle les membres ou les rôles
 * de cette communauté ? Reçue même quand l'admin n'en est pas membre (les
 * événements de la communauté, eux, ne lui arrivent pas).
 */
function auditTouchesMembers(entry: AuditLogLive, communityId: string): boolean {
  const details = entry.details as { communityId?: unknown } | null | undefined;
  const fromDetails = typeof details?.communityId === 'string' ? details.communityId : null;
  if ((entry.communityId ?? fromDetails) !== communityId) return false;
  return (
    entry.action.startsWith('role.') ||
    entry.action.startsWith('member.') ||
    entry.action === 'privilege_key.use'
  );
}

export function CommunityMembers(): JSX.Element {
  const { id: communityId } = useParams<{ id: string }>();
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyRole, setBusyRole] = useState<string | null>(null);
  const { socket } = useSocket();

  async function fetchAll(): Promise<void> {
    if (!communityId) return;
    try {
      const [ms, rs] = await Promise.all([
        api.adminListMembers(communityId),
        api.adminListRoles(communityId),
      ]);
      setMembers(ms);
      setRoles(rs);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  useEffect(() => {
    if (!socket || !communityId) return;
    // [2.10.0] Une même action arrive souvent deux fois (événement de la
    // communauté et entrée d'audit) : une seule relecture.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = (): void => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        void fetchAll();
      }, 150);
    };
    // [2.10.0] Seulement la communauté affichée (avant : toute communauté dont
    // l'admin est membre). Sans communauté (serveur ancien) : relecture.
    const ours = (cid?: string | null): boolean => !cid || cid === communityId;
    const onRole = (d?: { role?: { communityId?: string } }): void => {
      if (ours(d?.role?.communityId)) refresh();
    };
    const onCommunityEvent = (d?: { communityId?: string }): void => {
      if (ours(d?.communityId)) refresh();
    };
    const onMemberMove = (d?: { communityId?: string }): void => {
      if (d?.communityId === communityId) refresh();
    };
    const onAudit = (entry: AuditLogLive): void => {
      if (auditTouchesMembers(entry, communityId)) refresh();
    };
    socket.on('member:roles-updated', onCommunityEvent);
    socket.on('role:created', onRole);
    socket.on('role:updated', onRole);
    socket.on('role:deleted', onCommunityEvent);
    socket.on('community:member-joined', onMemberMove);
    socket.on('community:member-left', onMemberMove);
    socket.on('audit:new', onAudit);
    return () => {
      socket.off('member:roles-updated', onCommunityEvent);
      socket.off('role:created', onRole);
      socket.off('role:updated', onRole);
      socket.off('role:deleted', onCommunityEvent);
      socket.off('community:member-joined', onMemberMove);
      socket.off('community:member-left', onMemberMove);
      socket.off('audit:new', onAudit);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, communityId]);

  // Rôles hors @everyone (isManaged), affichés du plus haut au plus bas.
  const assignableRoles = useMemo(
    () =>
      [...roles]
        .filter((r) => !r.isManaged)
        .sort((a, b) => b.position - a.position || a.name.localeCompare(b.name)),
    [roles],
  );
  const selected = members.find((m) => m.id === selectedId) ?? null;

  async function toggleRole(member: AdminMember, role: AdminRole): Promise<void> {
    if (!communityId) return;
    const has = member.roleIds.includes(role.id);
    const prev = members;
    const nextRoleIds = has
      ? member.roleIds.filter((id) => id !== role.id)
      : [...member.roleIds, role.id];
    setMembers((ms) => ms.map((m) => (m.id === member.id ? { ...m, roleIds: nextRoleIds } : m)));
    setBusyRole(role.id);
    try {
      if (has) {
        await api.adminRevokeRole(communityId, member.userId, role.id);
      } else {
        await api.adminAssignRole(communityId, member.userId, role.id);
      }
      setErr(null);
    } catch (e) {
      setMembers(prev); // rollback
      const msg =
        e instanceof ApiError ? `Échec (${e.status}) : ${e.message}` : (e as Error).message;
      setErr(msg);
    } finally {
      setBusyRole(null);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ marginTop: 0 }}>Membres & rôles</h1>
        <Link to="/communities" style={{ fontSize: 13, color: 'var(--color-accent)' }}>
          ← Communautés
        </Link>
        {communityId && (
          <>
            <Link
              to={`/communities/${communityId}/roles`}
              style={{ fontSize: 13, color: 'var(--color-accent)' }}
            >
              Rôles & permissions
            </Link>
            <Link
              to={`/communities/${communityId}/channels-acl`}
              style={{ fontSize: 13, color: 'var(--color-accent)' }}
            >
              ACL salons
            </Link>
          </>
        )}
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Cliquez un membre pour gérer ses rôles. L'attribution est persistée serveur et diffusée en
        temps réel.
      </p>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Membre</th>
                  <th style={th}>Rôles</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === m.id ? 'var(--color-surface)' : 'transparent',
                    }}
                  >
                    <td style={td}>
                      <strong>{memberLabel(m)}</strong>
                      {m.user?.username && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          @{m.user.username}
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {m.roleIds.length === 0
                        ? '—'
                        : m.roleIds
                            .map((id) => roles.find((r) => r.id === id)?.name ?? '?')
                            .join(' · ')}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        type="button"
                        style={btnGhost}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(m.id);
                        }}
                      >
                        Attribuer rôles
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr>
                    <td style={td} colSpan={3}>
                      Aucun membre.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selected && (
            <div
              style={{
                flex: '1 1 340px',
                minWidth: 300,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                padding: 16,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Rôles de « {memberLabel(selected)} »</h3>
              {assignableRoles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                  Aucun rôle assignable (créez-en dans « Rôles & permissions »).
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {assignableRoles.map((r) => {
                    const checked = selected.roleIds.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                          fontSize: 13,
                          cursor: busyRole ? 'wait' : 'pointer',
                          opacity: busyRole && busyRole !== r.id ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busyRole !== null}
                          onChange={() => void toggleRole(selected, r)}
                        />
                        <span
                          style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: 3,
                            background: colorIntToHex(r.color),
                            border: '1px solid var(--color-border)',
                          }}
                        />
                        {r.name}
                      </label>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <button type="button" style={btnGhost} onClick={() => setSelectedId(null)}>
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
